import React, { useRef, useState } from "react";
import CustomModal from "../Modal";
import CustomButton from "../Button";
import { VscLoading } from "react-icons/vsc";
import { PDFDocument } from "pdf-lib";
import JSZip from "jszip";
import { SAFETY_INITIATIVES } from "../../util/certificateGenerator";
import { captureCertificateElement } from "../../util/certificateExport";
import CertificateCard from "../CertificateTemplate";

interface CertificateModalProps {
  open: boolean;
  onClose: () => void;
  employeeName: string;
  companyName: string;
}

const CertificateModal: React.FC<CertificateModalProps> = ({
  open,
  onClose,
  employeeName,
  companyName,
}) => {
  const [singleLoadingId, setSingleLoadingId] = useState<string | null>(null);
  const [allLoading, setAllLoading] = useState(false);
  const certificateRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const getSafeName = (value: string) => value.trim().replace(/\s+/g, "_");

  const renderCertificateCanvas = async (initiativeId: string) => {
    const certNode = certificateRefs.current[initiativeId];
    if (!certNode) {
      throw new Error("Certificate node not found");
    }

    return captureCertificateElement(certNode);
  };

  const canvasToPdfBlob = async (canvas: HTMLCanvasElement): Promise<Blob> => {
    const pngBlob: Blob = await new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Failed to create PNG blob from certificate"));
      }, "image/png");
    });
    const pngBytes = await pngBlob.arrayBuffer();
    const pdfDoc = await PDFDocument.create();
    const pngImage = await pdfDoc.embedPng(pngBytes);
    const page = pdfDoc.addPage([canvas.width, canvas.height]);
    page.drawImage(pngImage, {
      x: 0,
      y: 0,
      width: canvas.width,
      height: canvas.height,
    });
    const pdfBytes = await pdfDoc.save();
    return new Blob([pdfBytes], { type: "application/pdf" });
  };

  const downloadBlob = (blob: Blob, fileName: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownloadSingle = async (initiativeId: string) => {
    setSingleLoadingId(initiativeId);
    try {
      const initiative = SAFETY_INITIATIVES.find((i) => i.id === initiativeId);
      if (!initiative) return;

      const canvas = await renderCertificateCanvas(initiativeId);
      const pdfBlob = await canvasToPdfBlob(canvas);
      const fileName = `${getSafeName(employeeName)}_${initiative.id}.pdf`;
      downloadBlob(pdfBlob, fileName);
    } catch (error) {
      console.error("Error downloading certificate:", error);
    } finally {
      setSingleLoadingId(null);
    }
  };

  const handleDownloadAll = async () => {
    setAllLoading(true);
    try {
      const zip = new JSZip();
      for (const initiative of SAFETY_INITIATIVES) {
        const canvas = await renderCertificateCanvas(initiative.id);
        const pdfBlob = await canvasToPdfBlob(canvas);
        zip.file(`${getSafeName(employeeName)}_${initiative.id}.pdf`, pdfBlob);
      }
      const zipBlob = await zip.generateAsync({ type: "blob" });
      downloadBlob(zipBlob, `${getSafeName(employeeName)}_certificates.zip`);
    } catch (error) {
      console.error("Error downloading certificates:", error);
    } finally {
      setAllLoading(false);
    }
  };

  return (
    <CustomModal open={open} onClose={onClose} label="Safety Recognition Certificates">
      <div className="certificate-modal flex flex-col gap-4 mt-4">
        <div className="flex justify-between items-center">
          <div>
            <p className="text-primaryText text-lg font-semibold">
              {employeeName}
            </p>
            <p className="text-grayLight text-sm">
              {companyName}
            </p>
          </div>
          <CustomButton
            title={
              allLoading ? (
                <VscLoading className="animate-spin text-xl" />
              ) : (
                "Download All (ZIP + PDF)"
              )
            }
            onClick={handleDownloadAll}
            disabled={allLoading || !!singleLoadingId}
            className="!py-2"
          />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 overflow-y-auto" style={{ maxHeight: "60vh" }}>
          {SAFETY_INITIATIVES.map((initiative) => (
            <div key={initiative.id} className="bg-primary rounded-lg p-3 flex flex-col gap-2 hover:bg-gray/50 transition-colors">
              <div
                className="relative w-full certificate-capture"
                ref={(node) => {
                  certificateRefs.current[initiative.id] = node;
                }}
              >
                <CertificateCard
                  cert={initiative}
                  recipientName={employeeName}
                  elementId={`certificate-${initiative.id}`}
                  className="certificate--modal"
                />
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-primaryText text-sm font-medium">
                  {initiative.title}
                </p>
              </div>
              <div className="grid grid-cols-1 gap-2">
                <CustomButton
                  title={
                    singleLoadingId === initiative.id ? (
                      <VscLoading className="animate-spin" />
                    ) : (
                      "Download PDF"
                    )
                  }
                  onClick={() => handleDownloadSingle(initiative.id)}
                  disabled={allLoading || singleLoadingId === initiative.id}
                  className="!py-1 !text-sm"
                />
              </div>
            </div>
          ))}
        </div>

        <div className="text-center text-grayLight text-xs pt-2 border-t border-gray/30">
          OnMyWay Safety Platform • Professional Recognition Certificates
        </div>
      </div>
    </CustomModal>
  );
};

export default CertificateModal;
